import { useCallback, useEffect, useRef, useState } from "react";
import { SearchableOption } from "@core/components/SearchableListPicker";
import { fetchEntityUsers } from "@features/user/api/userApi";
import { EntityUser } from "@features/user/types/user";
import {
    INVITE_USER_SEARCH_DEBOUNCE_MS,
    INVITE_USER_SEARCH_MIN_CHARS,
    INVITE_USER_SEARCH_PER_PAGE,
} from "@features/admin/invitations/constants/inviteModal";

export interface InviteUserSearchResult {
    options: SearchableOption[];
    usersByUid: Record<number, EntityUser>;
    loading: boolean;
    error: boolean;
    setQuery: (query: string) => void;
    clear: () => void;
}

function toOption(user: EntityUser): SearchableOption {
    const name = [user.fname, user.lname].filter(Boolean).join(" ").trim() || user.username || user.email;
    return {
        value: user.uid,
        label: name,
        sublabel: `${user.email} · TRN ${user.trn}`,
    };
}

/**
 * Debounced freetext search of people in the inviter's workspace (includeChildren
 * for nation/district). Ignores stale responses via a monotonic request id.
 */
export function useInviteUserSearch(
    ownScope: string,
    ownScopeRefId: number,
    enabled: boolean
): InviteUserSearchResult {
    const [query, setQueryState] = useState("");
    const [options, setOptions] = useState<SearchableOption[]>([]);
    const [usersByUid, setUsersByUid] = useState<Record<number, EntityUser>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const requestIdRef = useRef(0);

    const clear = useCallback(() => {
        requestIdRef.current += 1;
        setQueryState("");
        setOptions([]);
        setUsersByUid({});
        setLoading(false);
        setError(false);
    }, []);

    const setQuery = useCallback((next: string) => {
        setQueryState(next);
    }, []);

    useEffect(() => {
        if (!enabled || !(ownScopeRefId > 0) || !ownScope) {
            clear();
            return;
        }

        const trimmed = query.trim();
        if (trimmed.length < INVITE_USER_SEARCH_MIN_CHARS) {
            requestIdRef.current += 1;
            setOptions([]);
            setUsersByUid({});
            setLoading(false);
            setError(false);
            return;
        }

        const requestId = ++requestIdRef.current;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError(false);
            try {
                const params: Record<string, string> = {
                    freetext: trimmed,
                    page: "1",
                    perPage: String(INVITE_USER_SEARCH_PER_PAGE),
                };
                if (ownScope === "NATION" || ownScope === "DISTRICT") {
                    params.includeChildren = "true";
                }

                const response = await fetchEntityUsers(ownScope, ownScopeRefId, params);
                if (requestId !== requestIdRef.current) return;

                if (!response.isSuccess()) {
                    setOptions([]);
                    setUsersByUid({});
                    setError(true);
                    return;
                }

                const rows = response.getData()?.data ?? [];
                const byUid: Record<number, EntityUser> = {};
                const nextOptions: SearchableOption[] = [];
                for (const user of rows) {
                    byUid[user.uid] = user;
                    nextOptions.push(toOption(user));
                }
                setUsersByUid(byUid);
                setOptions(nextOptions);
                setError(false);
            } catch {
                if (requestId !== requestIdRef.current) return;
                setOptions([]);
                setUsersByUid({});
                setError(true);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        }, INVITE_USER_SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [query, ownScope, ownScopeRefId, enabled, clear]);

    return { options, usersByUid, loading, error, setQuery, clear };
}
